import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const CAP_PRESET_LABELS: Record<string, string> = {
  conservative: '控えめ（約100回答/月）',
  standard: '標準（約500回答/月）',
  generous: '多め（約1,000回答/月）',
  large: '大量（約3,000回答/月）',
  unlimited: '無制限',
};

interface OverageSettingsProps {
  domainId: string;
  domain: any;
  onSave: (data: any, fieldName: string) => void;
  savingField: string | null;
}

export const OverageSettings: React.FC<OverageSettingsProps> = ({ domainId, domain, onSave, savingField }) => {
  const settings = domain?.settings || {};

  const [enabled, setEnabled] = useState(settings.overage_enabled || false);
  const [capPreset, setCapPreset] = useState<string>(settings.overage_cap_preset || 'standard');
  const [stopBehavior, setStopBehavior] = useState<string>(settings.overage_stop_behavior || 'message');

  useEffect(() => {
    const s = domain?.settings || {};
    setEnabled(s.overage_enabled || false);
    setCapPreset(s.overage_cap_preset || 'standard');
    setStopBehavior(s.overage_stop_behavior || 'message');
  }, [domain?.settings]);

  const save = (overrides: Record<string, any>) => {
    onSave({ settings: { overage_enabled: enabled, overage_cap_preset: capPreset, overage_stop_behavior: stopBehavior, ...overrides } }, 'overage_enabled');
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="overage-settings">
      {/* トグル行 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            自動延長設定
            {savingField === 'overage_enabled' && (
              <span data-testid="settings-saving-indicator" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#0a7a72', fontWeight: 400 }}>
                <Loader2 className="h-3 w-3 animate-spin" />保存中…
              </span>
            )}
          </div>
          <div style={{ fontSize: '13px', color: '#64748B' }}>
            月間メッセージ上限を超えた場合、自動で回答を延長します。超過分は¥3/回答で課金されます。
          </div>
        </div>
        <div
          onClick={() => { const next = !enabled; setEnabled(next); save({ overage_enabled: next }); }}
          style={{ width: '44px', height: '24px', borderRadius: '12px', background: enabled ? '#0a7a72' : '#E2E8F0', cursor: 'pointer', opacity: savingField === 'overage_enabled' ? 0.6 : 1, position: 'relative', transition: 'background 0.2s', flexShrink: 0, pointerEvents: savingField === 'overage_enabled' ? 'none' : 'auto' }}
          data-testid="overage-enabled-switch"
        >
          <div style={{ position: 'absolute', top: '3px', left: enabled ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
      </div>

      {/* 詳細設定（enabled時のみ） */}
      {enabled && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748B', marginBottom: '4px' }}>キャップ</label>
            <select
              value={capPreset}
              onChange={(e) => { setCapPreset(e.target.value); save({ overage_cap_preset: e.target.value }); }}
              data-testid="overage-cap-select"
              style={{ fontSize: '14px', padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', color: '#0F172A', background: '#fff', minWidth: '200px' }}
            >
              {Object.entries(CAP_PRESET_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#64748B', marginBottom: '4px' }}>上限到達時の動作</label>
            <select
              value={stopBehavior}
              onChange={(e) => { setStopBehavior(e.target.value); save({ overage_stop_behavior: e.target.value }); }}
              data-testid="overage-action-select"
              style={{ fontSize: '14px', padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '8px', outline: 'none', fontFamily: 'inherit', color: '#0F172A', background: '#fff', minWidth: '180px' }}
            >
              <option value="message">固定メッセージを表示</option>
              <option value="hide">チャットを非表示</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
